<div id="profile-fields" class="card bg-base-100 shadow-xl" data-testid="profile-fields-card">
  <div class="card-body">
    <h2 class="card-title text-base-content">
      <app-icon name="badge" class="mr-2"></app-icon>
      My Information
    </h2>

    @if (isLoading) {
      <div data-testid="profile-fields-loading" class="flex items-center gap-2 py-4 text-base-content/70">
        <span class="loading loading-spinner loading-sm"></span>
        Loading your information...
      </div>
    }

    @if (!isLoading && loadError) {
      <div data-testid="profile-fields-error" class="alert alert-error mt-2" role="alert">
        <app-icon name="error"></app-icon>
        <span>{{ loadError }}</span>
      </div>
    }

    @if (!isLoading && !hasOrganization) {
      <div data-testid="profile-fields-no-organization" class="py-4 text-center text-base-content/60">
        <app-icon name="business_center" class="text-3xl mb-2"></app-icon>
        <p>Your account needs to be assigned to an organization before profile information is available.</p>
      </div>
    }

    @if (!isLoading && hasOrganization && !loadError && fields.length === 0) {
      <p data-testid="profile-fields-empty" class="py-4 text-base-content/60">
        Your organization has not requested any additional profile information.
      </p>
    }

    @if (!isLoading && hasOrganization && !loadError && fields.length > 0) {
      <div class="divide-y divide-base-300 mt-2">
        @for (field of fields; track field.fieldId) {
          <div [attr.data-testid]="'profile-field-row-' + field.fieldKey" class="py-3">
            <div class="flex items-start justify-between flex-wrap gap-2">
              <div class="min-w-0 flex-1">
                <label class="text-sm font-medium text-base-content/70 flex items-center gap-2">
                  {{ field.fieldLabel }}
                  @if (isRequestedAndBlank(field)) {
                    <span [attr.data-testid]="'profile-field-requested-' + field.fieldKey"
                      class="badge badge-sm badge-warning badge-outline">{{ fieldBadgeLabel }}</span>
                  }
                </label>
                @if (field.description) {
                  <p class="text-xs text-base-content/50 mt-0.5">{{ field.description }}</p>
                }

                @if (!isEditing(field)) {
                  <p [attr.data-testid]="'profile-field-value-' + field.fieldKey"
                    class="mt-1 text-base-content"
                    [ngClass]="{ 'opacity-60 italic': displayValue(field) === 'Not provided' }">
                    {{ displayValue(field) }}
                  </p>
                }

                @if (isEditing(field)) {
                  <div class="mt-2 space-y-2">
                    <!-- Month + year picker: deliberately month and year only, no day. -->
                    @if (isMonthYear(field)) {
                      <div class="flex gap-2 flex-wrap">
                        <select data-testid="profile-field-month-select"
                          class="select select-bordered select-sm"
                          [disabled]="edit!.saving"
                          [(ngModel)]="edit!.draftMonth"
                          [attr.aria-label]="field.fieldLabel + ' month'">
                          <option value="">Month</option>
                          @for (m of months; track m; let i = $index) {
                            <option [value]="i + 1">{{ m }}</option>
                          }
                        </select>
                        <select data-testid="profile-field-year-select"
                          class="select select-bordered select-sm"
                          [disabled]="edit!.saving"
                          [(ngModel)]="edit!.draftYear"
                          [attr.aria-label]="field.fieldLabel + ' year'">
                          <option value="">Year</option>
                          @for (y of years; track y) {
                            <option [value]="y">{{ y }}</option>
                          }
                        </select>
                      </div>
                    } @else if (field.fieldType === 'dropdown') {
                      <select [attr.data-testid]="'profile-field-input-' + field.fieldKey"
                        class="select select-bordered select-sm w-full max-w-xs"
                        [disabled]="edit!.saving"
                        [(ngModel)]="edit!.draft"
                        [attr.aria-label]="field.fieldLabel">
                        <option value="">Select...</option>
                        @for (opt of field.fieldOptions?.options ?? []; track opt.value) {
                          <option [value]="opt.value">{{ opt.label }}</option>
                        }
                      </select>
                    } @else if (field.fieldType === 'boolean') {
                      <select [attr.data-testid]="'profile-field-input-' + field.fieldKey"
                        class="select select-bordered select-sm"
                        [disabled]="edit!.saving"
                        [(ngModel)]="edit!.draft"
                        [attr.aria-label]="field.fieldLabel">
                        <option value="">Select...</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    } @else {
                      <input [attr.data-testid]="'profile-field-input-' + field.fieldKey"
                        class="input input-bordered input-sm w-full max-w-xs"
                        [type]="inputType(field)"
                        [disabled]="edit!.saving"
                        [placeholder]="field.validationRules?.placeholder || ''"
                        [(ngModel)]="edit!.draft"
                        [attr.aria-label]="field.fieldLabel" />
                    }

                    @if (edit!.error) {
                      <p [attr.data-testid]="'profile-field-error-' + field.fieldKey"
                        class="text-sm text-error" role="alert">{{ edit!.error }}</p>
                    }

                    <div class="flex gap-2">
                      <button [attr.data-testid]="'profile-field-save-' + field.fieldKey"
                        class="btn btn-primary btn-sm"
                        [disabled]="edit!.saving"
                        (click)="save(field)">
                        @if (edit!.saving) {
                          <span class="loading loading-spinner loading-xs"></span>
                          Saving...
                        } @else {
                          Save
                        }
                      </button>
                      <button class="btn btn-ghost btn-sm"
                        [attr.data-testid]="'profile-field-cancel-' + field.fieldKey"
                        [disabled]="edit!.saving"
                        (click)="cancelEdit()">Cancel</button>
                    </div>
                  </div>
                }
              </div>

              @if (!isEditing(field)) {
                <button [attr.data-testid]="'profile-field-edit-' + field.fieldKey"
                  class="btn btn-ghost btn-sm"
                  [attr.aria-label]="'Edit ' + field.fieldLabel"
                  (click)="startEdit(field)">
                  <app-icon name="edit" class="mr-1"></app-icon>
                  Edit
                </button>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>
</div>
